import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Col, Row, Card, Button, Table, Spinner, Form, Dropdown, ButtonGroup } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";

export default () => {
  const location = useLocation();
  const [grille, setGrille] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draws, setDraws] = useState([]);
  const [selectedDrawId, setSelectedDrawId] = useState("");
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  
  // Page tab modes: general, par_tirage, prime_agent, prime_par_tirage
  const [activeTab, setActiveTab] = useState("general"); 

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const drawParam = params.get("draw");
    if (tabParam) {
      setActiveTab(tabParam);
    } else if (drawParam) {
      setActiveTab("par_tirage");
    }
    if (drawParam) {
      setSelectedDrawId(drawParam);
    }
  }, [location.search]); 
  const [isEditing, setIsEditing] = useState(false);
  const [editingType, setEditingType] = useState(null); // 'Borlette', 'Loto 3', etc.

  // Form states for editing
  const [field1, setField1] = useState("");
  const [field2, setField2] = useState("");
  const [field3, setField3] = useState("");

  // Load draws filter options
  const fetchDraws = async () => {
    try {
      const res = await client("reports/filters/");
      if (res && res.draws) {
        setDraws(res.draws);
        if (res.draws.length > 0) {
          setSelectedDrawId(res.draws[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch draws list for configuration:", err);
    }
  };

  const fetchAgents = async () => {
    try {
      const data = await client("agents/");
      const list = data ? (data.results || (Array.isArray(data) ? data : [])) : [];
      setAgents(list);
      if (list.length > 0) {
        setSelectedAgentId(list[0].id);
      }
    } catch (err) {
      console.error("Failed to fetch agents list for configuration:", err);
    }
  };

  const fetchGrille = async (tab, drawId, agentId) => {
    setLoading(true);
    try {
      let url = "grille-gains/";
      if (tab === "general") {
        url += "?target_level=TENANT";
      } else if (tab === "par_tirage" && drawId) {
        url += `?target_level=TENANT&draw=${drawId}`;
      } else if (tab === "prime_agent" && agentId) {
        url += `?target_level=AGENT&agent=${agentId}`;
      } else if (tab === "prime_par_tirage" && agentId && drawId) {
        url += `?target_level=AGENT&agent=${agentId}&draw=${drawId}`;
      }

      const data = await client(url);
      const list = data ? (data.results || (Array.isArray(data) ? data : [])) : [];
      if (list.length > 0) {
        setGrille(list[0]);
      } else {
        // Fallback default values
        setGrille({
          id: null,
          borlette_win1: 60,
          borlette_win2: 20,
          borlette_win3: 10,
          loto3_multiplier: 500,
          mariage_multiplier: 1000,
          loto4_opt1: 5000,
          loto4_opt2: 5000,
          loto4_opt3: 5000,
          loto5_opt1: 25000,
          loto5_opt2: 25000,
          loto5_opt3: 25000
        });
      }
    } catch (err) {
      console.error("Failed to fetch grille gains:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDraws();
    fetchAgents();
  }, []);

  useEffect(() => {
    if (activeTab === "general") {
      fetchGrille("general", null, null);
    } else if (activeTab === "par_tirage" && selectedDrawId) {
      fetchGrille("par_tirage", selectedDrawId, null);
    } else if (activeTab === "prime_agent" && selectedAgentId) {
      fetchGrille("prime_agent", null, selectedAgentId);
    } else if (activeTab === "prime_par_tirage" && selectedAgentId && selectedDrawId) {
      fetchGrille("prime_par_tirage", selectedDrawId, selectedAgentId);
    }
  }, [activeTab, selectedDrawId, selectedAgentId]);

  const getRows = () => {
    if (!grille) return [];
    return [
      { type: "Borlette", val: `${grille.borlette_win1 || 0}|${grille.borlette_win2 || 0}|${grille.borlette_win3 || 0}` },
      { type: "Loto 3", val: `${grille.loto3_multiplier || 0}` },
      { type: "Mariage", val: `${grille.mariage_multiplier || 0}` },
      { type: "Mariage Gratuit", val: `${grille.mariage_gratuit_multiplier || 1000}` },
      { type: "Loto 4", val: `${grille.loto4_opt1 || 0}|${grille.loto4_opt2 || 0}|${grille.loto4_opt3 || 0}` },
      { type: "Loto 5", val: `${grille.loto5_opt1 || 0}|${grille.loto5_opt2 || 0}|${grille.loto5_opt3 || 0}` }
    ];
  };

  const handleEditClick = (type, val) => {
    setIsEditing(true);

    if (type === "Borlette") {
      setEditingType("Borlette");
      setField1(grille.borlette_win1 || "");
      setField2(grille.borlette_win2 || "");
      setField3(grille.borlette_win3 || "");
    } else if (type === "Loto 3") {
      setEditingType("Loto 3");
      setField1(grille.loto3_multiplier || "");
      setField2("");
      setField3("");
    } else if (type === "Mariage") {
      setEditingType("Mariage");
      setField1(grille.mariage_multiplier || "");
      setField2("");
      setField3("");
    } else if (type === "Mariage Gratuit") {
      setEditingType("Mariage Gratuit");
      setField1(grille.mariage_gratuit_multiplier || "");
      setField2("");
      setField3("");
    } else if (type === "Loto 4") {
      setEditingType("Loto 4");
      setField1(grille.loto4_opt1 || "");
      setField2(grille.loto4_opt2 || "");
      setField3(grille.loto4_opt3 || "");
    } else if (type === "Loto 5") {
      setEditingType("Loto 5");
      setField1(grille.loto5_opt1 || "");
      setField2(grille.loto5_opt2 || "");
      setField3(grille.loto5_opt3 || "");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!grille) return;

    let updatedFields = {};
    if (editingType === "Borlette") {
      updatedFields = {
        borlette_win1: parseInt(field1) || 0,
        borlette_win2: parseInt(field2) || 0,
        borlette_win3: parseInt(field3) || 0
      };
    } else if (editingType === "Loto 3") {
      updatedFields = { loto3_multiplier: parseInt(field1) || 0 };
    } else if (editingType === "Mariage") {
      updatedFields = { mariage_multiplier: parseInt(field1) || 0 };
    } else if (editingType === "Mariage Gratuit") {
      updatedFields = { mariage_gratuit_multiplier: parseInt(field1) || 0 };
    } else if (editingType === "Loto 4") {
      updatedFields = {
        loto4_opt1: parseInt(field1) || 0,
        loto4_opt2: parseInt(field2) || 0,
        loto4_opt3: parseInt(field3) || 0
      };
    } else if (editingType === "Loto 5") {
      updatedFields = {
        loto5_opt1: parseInt(field1) || 0,
        loto5_opt2: parseInt(field2) || 0,
        loto5_opt3: parseInt(field3) || 0
      };
    }

    const payload = {
      ...grille,
      ...updatedFields
    };

    if (payload.id === null) delete payload.id;
    if (payload.draw === null || payload.draw === "") delete payload.draw;

    try {
      if (grille.id) {
        // PUT request
        await client(`grille-gains/${grille.id}/`, {
          method: "PUT",
          body: payload
        });
      } else {
        // POST request
        const postBody = {
          ...payload,
          target_level: (activeTab === "general" || activeTab === "par_tirage") ? "TENANT" : "AGENT"
        };
        if (activeTab === "prime_agent" || activeTab === "prime_par_tirage") {
          postBody.agent = selectedAgentId;
        }
        if (activeTab === "par_tirage" || activeTab === "prime_par_tirage") {
          postBody.draw = selectedDrawId;
        }
        await client(`grille-gains/`, {
          method: "POST",
          body: postBody
        });
      }
      setIsEditing(false);
      fetchGrille(activeTab, selectedDrawId, selectedAgentId);
    } catch (err) {
      console.error("Failed to save grille configuration:", err);
      alert("Erreur lors de l'enregistrement de la grille.");
    }
  };

  const isMultiField = (type) => {
    return type === "Borlette" || type === "Loto 4" || type === "Loto 5";
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <Card className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
        <Card.Header className="bg-white border-0 pt-4 pb-0">
          <h4 style={{ fontWeight: 'bold', color: '#333', margin: 0 }}>Primes</h4>
        </Card.Header>

        <Card.Body>
          {/* Top navigation tabs matching General (blue), Par tirage (green), Prime Agent (orange), Prime par tirage (teal) */}
          <div className="d-flex mb-4 flex-wrap" style={{ gap: '10px' }}>
            <Button
              style={{
                backgroundColor: activeTab === "general" ? '#007BFF' : 'transparent',
                borderColor: '#007BFF',
                color: activeTab === "general" ? '#FFF' : '#007BFF',
                borderRadius: '4px',
                fontSize: '0.85rem',
                padding: '6px 16px',
                fontWeight: '500'
              }}
              onClick={() => { setActiveTab("general"); setIsEditing(false); }}
            >
              Général
            </Button>
            <Button
              style={{
                backgroundColor: activeTab === "par_tirage" ? '#28A745' : 'transparent',
                borderColor: '#28A745',
                color: activeTab === "par_tirage" ? '#FFF' : '#28A745',
                borderRadius: '4px',
                fontSize: '0.85rem',
                padding: '6px 16px',
                fontWeight: '500'
              }}
              onClick={() => { setActiveTab("par_tirage"); setIsEditing(false); }}
            >
              Par Tirage
            </Button>
            <Button
              style={{
                backgroundColor: activeTab === "prime_agent" ? '#FD7E14' : 'transparent',
                borderColor: '#FD7E14',
                color: activeTab === "prime_agent" ? '#FFF' : '#FD7E14',
                borderRadius: '4px',
                fontSize: '0.85rem',
                padding: '6px 16px',
                fontWeight: '500'
              }}
              onClick={() => { setActiveTab("prime_agent"); setIsEditing(false); }}
            >
              Prime Agent
            </Button>
            <Button
              style={{
                backgroundColor: activeTab === "prime_par_tirage" ? '#17A2B8' : 'transparent',
                borderColor: '#17A2B8',
                color: activeTab === "prime_par_tirage" ? '#FFF' : '#17A2B8',
                borderRadius: '4px',
                fontSize: '0.85rem',
                padding: '6px 16px',
                fontWeight: '500'
              }}
              onClick={() => { setActiveTab("prime_par_tirage"); setIsEditing(false); }}
            >
              Prime par tirage
            </Button>
          </div>

          {/* Selectors for Agent/Draw based on active tab */}
          {!isEditing && (
            <div className="mb-4 d-flex align-items-center flex-wrap" style={{ gap: '16px' }}>
              {(activeTab === "prime_agent" || activeTab === "prime_par_tirage") && (
                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                  <Form.Label className="small fw-bold mb-0" style={{ color: '#111' }}>Sélectionner un Agent :</Form.Label>
                  <Form.Select
                    style={{ width: '250px', borderRadius: '4px', borderColor: '#CCC' }}
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                  >
                    {agents.map(a => (
                      <option key={a.id} value={a.id}>{a.username} ({a.branch_name || "Principale"})</option>
                    ))}
                  </Form.Select>
                </div>
              )}

              {(activeTab === "par_tirage" || activeTab === "prime_par_tirage") && (
                <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                  <Form.Label className="small fw-bold mb-0" style={{ color: '#111' }}>Sélectionner un Tirage :</Form.Label>
                  <Form.Select
                    style={{ width: '250px', borderRadius: '4px', borderColor: '#CCC' }}
                    value={selectedDrawId}
                    onChange={(e) => setSelectedDrawId(e.target.value)}
                  >
                    {draws.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </Form.Select>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <div className="text-muted small mt-2">Chargement des primes...</div>
            </div>
          ) : !isEditing ? (
            /* LIST VIEW (Table with NO Code column) */
            <div className="table-responsive">
              <Table bordered hover className="align-items-center mb-0" style={{ borderCollapse: 'collapse', borderColor: '#EAEAEA' }}>
                <thead>
                  <tr style={{ backgroundColor: '#FAF9F9' }}>
                    <th style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#111', fontWeight: 'bold' }}>Type</th>
                    <th style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#111', fontWeight: 'bold' }}>Prime</th>
                    <th style={{ padding: '12px 16px', borderLeft: 'none' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {getRows().map((row, idx) => (
                    <tr key={idx} style={{ verticalAlign: 'middle' }}>
                      <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#333' }}>{row.type}</td>
                      <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#333', fontWeight: '500' }}>{row.val}</td>
                      
                      {/* Split Dropdown Action Button */}
                      <td style={{ padding: '12px 16px', width: '120px', borderLeft: 'none' }}>
                        <Dropdown as={ButtonGroup} size="sm">
                          <Button 
                            style={{ 
                              backgroundColor: '#007BFF', 
                              borderColor: '#007BFF', 
                              color: '#FFF',
                              fontSize: '0.78rem',
                              padding: '5px 12px',
                              borderTopLeftRadius: '4px',
                              borderBottomLeftRadius: '4px'
                            }}
                            onClick={() => handleEditClick(row.type, row.val)}
                          >
                            Action
                          </Button>
                          <Dropdown.Toggle 
                            split 
                            style={{ 
                              backgroundColor: '#0056b3', 
                              borderColor: '#0056b3', 
                              color: '#FFF',
                              padding: '5px 8px',
                              borderTopRightRadius: '4px',
                              borderBottomRightRadius: '4px'
                            }}
                          >
                            <FontAwesomeIcon icon={faCaretDown} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ minWidth: '80px', padding: '4px 0' }}>
                            <Dropdown.Item 
                              style={{ fontSize: '0.8rem', padding: '6px 12px' }} 
                              onClick={() => handleEditClick(row.type, row.val)}
                            >
                              Modifier
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            /* EDIT VIEW (Modifier une prime) */
            <div>
              <style>{`
                .custom-input::placeholder {
                  font-size: 0.78rem !important;
                  color: #999999 !important;
                  opacity: 1 !important;
                }
                .custom-input {
                  font-size: 0.85rem !important;
                  color: #111111 !important;
                }
              `}</style>

              <div className="d-flex mb-3">
                <Button 
                  variant="outline-primary"
                  style={{
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    padding: '6px 20px',
                    fontWeight: '500'
                  }}
                  onClick={() => setIsEditing(false)}
                >
                  Lister
                </Button>
              </div>

              <h5 className="mb-3" style={{ fontWeight: 'bold', color: '#111' }}>Modifier une prime</h5>

              <Form onSubmit={handleSave}>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Description</Form.Label>
                  <Form.Control 
                    type="text"
                    disabled
                    style={{ backgroundColor: '#EAEAEA', borderColor: '#CCC', borderRadius: '4px', fontSize: '0.85rem', color: '#666' }}
                    value={editingType}
                  />
                </Form.Group>

                {isMultiField(editingType) ? (
                  /* 3 Fields Layout for Borlette, L4 and L5 */
                  <Row className="mb-3">
                    <Col xs={12} md={4} className="mb-3 mb-md-0">
                      <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>
                        {editingType === "Borlette" ? "1er lot" : "Option 1"}
                      </Form.Label>
                      <div className="position-relative d-flex align-items-center">
                        <Form.Control 
                          type="text"
                          className="custom-input"
                          style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                          value={field1}
                          onChange={(e) => setField1(e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        <FontAwesomeIcon 
                          icon={faCheck} 
                          className="position-absolute" 
                          style={{ right: '12px', color: field1 ? '#28A745' : '#CCCCCC', transition: 'color 0.2s ease' }} 
                        />
                      </div>
                    </Col>

                    <Col xs={12} md={4} className="mb-3 mb-md-0">
                      <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>
                        {editingType === "Borlette" ? "2eme lot" : "Option 2"}
                      </Form.Label>
                      <div className="position-relative d-flex align-items-center">
                        <Form.Control 
                          type="text"
                          className="custom-input"
                          style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                          value={field2}
                          onChange={(e) => setField2(e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        <FontAwesomeIcon 
                          icon={faCheck} 
                          className="position-absolute" 
                          style={{ right: '12px', color: field2 ? '#28A745' : '#CCCCCC', transition: 'color 0.2s ease' }} 
                        />
                      </div>
                    </Col>

                    <Col xs={12} md={4}>
                      <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>
                        {editingType === "Borlette" ? "3eme lot" : "Option 3"}
                      </Form.Label>
                      <div className="position-relative d-flex align-items-center">
                        <Form.Control 
                          type="text"
                          className="custom-input"
                          style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                          value={field3}
                          onChange={(e) => setField3(e.target.value.replace(/[^0-9]/g, ''))}
                        />
                        <FontAwesomeIcon 
                          icon={faCheck} 
                          className="position-absolute" 
                          style={{ right: '12px', color: field3 ? '#28A745' : '#CCCCCC', transition: 'color 0.2s ease' }} 
                        />
                      </div>
                    </Col>
                  </Row>
                ) : (
                  /* Single Field Layout for Loto 3, Mariage, etc. */
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold mb-1" style={{ color: '#111' }}>Prime</Form.Label>
                    <div className="position-relative d-flex align-items-center">
                      <Form.Control 
                        type="text"
                        className="custom-input"
                        style={{ borderColor: '#28A745', borderRadius: '4px', paddingRight: '36px' }}
                        value={field1}
                        onChange={(e) => setField1(e.target.value.replace(/[^0-9]/g, ''))}
                      />
                      <FontAwesomeIcon 
                        icon={faCheck} 
                        className="position-absolute" 
                        style={{ right: '12px', color: field1 ? '#28A745' : '#CCCCCC', transition: 'color 0.2s ease' }} 
                      />
                    </div>
                  </Form.Group>
                )}

                <Button
                  type="submit"
                  style={{
                    backgroundColor: '#007BFF',
                    borderColor: '#007BFF',
                    color: '#FFF',
                    fontWeight: '600',
                    padding: '10px 0',
                    borderRadius: '4px',
                    width: '100%',
                    letterSpacing: '0.5px',
                    marginTop: '10px'
                  }}
                >
                  Modifier
                </Button>
              </Form>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};
